import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Archive, ChevronRight, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '@/components/layout/PageHeader';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { Spinner } from '@/components/ui/Spinner';
import { StatCard } from '@/components/ui/StatCard';
import { usePageContext } from '@/hooks/usePageContext';
import type { FilterOption } from '@/types/filters.types';
import {
  getPackageManagementOptions,
  getPackageManagementRows,
  type PackageManagementFilters,
} from '@/services/packages-management.service';

import {
  EMPTY_PACKAGE_STATS,
  formatPackageDateInline,
  PACKAGE_FILTER_CONFIGS,
  PACKAGE_REMAINING_OPTIONS,
  PACKAGE_STATUS_OPTIONS,
  PACKAGE_STATUS_VARIANTS,
  packageStatusLabel,
} from './packagesManagementShared';
import { buildApiParams } from '@/lib/filterRegistry';
import {
  useFacilityOverrideValues,
  useLocalDashboardFilters,
} from '@/pages/dashboard/dashboardFilters';

export default function PackagesDashboardPage() {
  const navigate = useNavigate();
  const updateAIContext = usePageContext('Packages Dashboard');
  const filters = useLocalDashboardFilters(PACKAGE_FILTER_CONFIGS, 'packages');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filters.values);
  const baseFilters = useMemo(() => ({
    page: 1,
    perPage: 5,
    sortBy: 'createdAt',
    sortDir: 'desc' as const,
    searchType: '0' as const,
  }), []);
  const queryFilters = useMemo(
    () => buildApiParams(effectiveValues, filters.negations, baseFilters),
    [baseFilters, effectiveValues, filters.negations],
  );

  const packagesQuery = useQuery({
    queryKey: ['packages-dashboard-list', queryFilters],
    queryFn: () => getPackageManagementRows(queryFilters as PackageManagementFilters),
    staleTime: 60_000,
  });

  const optionsQuery = useQuery({
    queryKey: ['packages-dashboard-options'],
    queryFn: getPackageManagementOptions,
    staleTime: 300_000,
  });

  const stats = packagesQuery.data?.stats ?? EMPTY_PACKAGE_STATS;
  const recentPackages = packagesQuery.data?.items ?? [];
  const templates = optionsQuery.data?.templates ?? [];
  const facilities = optionsQuery.data?.facilities ?? [];
  const filterOptions = useMemo<Record<string, FilterOption[]>>(() => ({
    'package-type': templates.map((template) => ({ value: String(template.value), label: template.label })),
    'package-status': PACKAGE_STATUS_OPTIONS.map((item) => ({ value: item.value, label: item.label })),
    'package-remaining': PACKAGE_REMAINING_OPTIONS.map((item) => ({ value: item.value, label: item.label })),
    facility: facilities.map((facility) => ({ value: String(facility.value), label: facility.label })),
  }), [facilities, templates]);

  useEffect(() => {
    updateAIContext({
      dataSummary: `Active: ${stats.activeCount}, Declined: ${stats.declinedCount}, Inactive: ${stats.inactiveCount}, Recent Packages: ${recentPackages.length}`,
    });
  }, [recentPackages.length, stats.activeCount, stats.declinedCount, stats.inactiveCount, updateAIContext]);

  if (packagesQuery.isLoading && !packagesQuery.data) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" label="Loading packages..." />
      </div>
    );
  }

  return (
    <ListPageLayout
      filterSidebar={(
        <FilterSidebar
          filters={PACKAGE_FILTER_CONFIGS}
          values={effectiveValues}
          options={filterOptions}
          negations={filters.negations}
          onChange={filters.setFilter}
          onNegateChange={filters.setNegation}
          onClear={filters.clearFilter}
          lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          storageKey="packages"
          showAIAssistant={false}
        />
      )}
    >
      <div className="space-y-6 overflow-y-auto p-6">
      <PageHeader
        title="Packages"
        actions={(
          <Button onClick={() => navigate('/packages/all')}>
            All Packages
          </Button>
        )}
      />

      <MetricCardGrid columns={3}>
        <StatCard label="Active" value={stats.activeCount} tone="emerald" icon={<Package className="h-4 w-4" />} onClick={() => navigate('/packages/all')} />
        <StatCard label="Declined" value={stats.declinedCount} tone="amber" icon={<AlertTriangle className="h-4 w-4" />} />
        <StatCard label="Inactive" value={stats.inactiveCount} tone="slate" icon={<Archive className="h-4 w-4" />} />
      </MetricCardGrid>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="xl:col-span-4">
          <Card className="h-full">
            <CardHeader>
              <div>
                <CardTitle>Quick Access</CardTitle>
                <p className="mt-1 text-xs text-slate-500">Open the existing package management list, filters, and detail flow.</p>
              </div>
            </CardHeader>
            <CardBody className="space-y-3">
              <button
                type="button"
                onClick={() => navigate('/packages/all')}
                className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-4 py-3 text-left transition hover:border-primary/40 hover:bg-slate-50"
              >
                <div>
                  <p className="text-sm font-medium text-slate-800">All Packages</p>
                  <p className="mt-1 text-xs text-slate-500">Review the full package list with the current filters and row actions.</p>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-400" />
              </button>
            </CardBody>
          </Card>
        </div>

        <div className="xl:col-span-5">
          <Card className="h-full">
            <CardHeader
              action={(
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 px-2 text-xs text-primary"
                  onClick={() => navigate('/packages/all')}
                >
                  View All <ChevronRight className="h-3 w-3" />
                </Button>
              )}
            >
              <div>
                <CardTitle>Recent Packages</CardTitle>
                <p className="mt-1 text-xs text-slate-500">Most recent rows from the existing package management list.</p>
              </div>
            </CardHeader>
            <CardBody className="px-0 pb-0">
              <div className="divide-y divide-border">
                {packagesQuery.isLoading ? (
                  <div className="flex items-center justify-center px-5 py-8">
                    <Spinner size="sm" />
                  </div>
                ) : recentPackages.length === 0 ? (
                  <p className="px-5 py-8 text-center text-sm text-slate-500">No packages found.</p>
                ) : (
                  recentPackages.map((row) => (
                    <div key={row.patientPackageId} className="px-5 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800">{row.patientName}</p>
                          <p className="mt-1 text-xs text-slate-500">{row.type || 'Package'} • {formatPackageDateInline(row.createdAt)}</p>
                        </div>
                        <Badge variant={PACKAGE_STATUS_VARIANTS[row.status] ?? 'neutral'} dot>
                          {packageStatusLabel(row.status)}
                        </Badge>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="xl:col-span-3">
          <Card className="h-full">
            <CardHeader>
              <div>
                <CardTitle>Configured Options</CardTitle>
                <p className="mt-1 text-xs text-slate-500">Existing packages and facilities already available in the current filters.</p>
              </div>
            </CardHeader>
            <CardBody className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200 px-3 py-3">
                  <p className="text-xs uppercase tracking-wide text-slate-400">Packages</p>
                  <p className="mt-1 text-lg font-semibold text-slate-800">{templates.length}</p>
                </div>
                <div className="rounded-lg border border-slate-200 px-3 py-3">
                  <p className="text-xs uppercase tracking-wide text-slate-400">Facilities</p>
                  <p className="mt-1 text-lg font-semibold text-slate-800">{facilities.length}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {templates.slice(0, 4).map((item) => (
                  <span key={item.value} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">
                    {item.label}
                  </span>
                ))}
                {templates.length > 4 && (
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">
                    +{templates.length - 4} more
                  </span>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
      </div>
    </ListPageLayout>
  );
}
